import { homeRoute, orderCenterRoutes, useRouter } from '@src/routes'
import { useState, useEffect } from 'react'
import messages from '@src/routes/OrderCenter/messages'
import mainMessages from '@src/utils/messages'

import { MessageDescriptor } from 'react-intl'
import { UserUnion, useCurrentUser } from './useCurrentUser'
import { UserSession } from './userSession'

declare type PrimitiveType = string | number | boolean | null | undefined | Date
declare type FormatXMLElementFn<T, R = string | (string | T)[]> = (
  parts: (string | T)[]
) => R

interface SearchItem {
  url?: string
  label: string
  gaLabel?: string
}

export const useOrderCenterBreadCrumbItems = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>
  ) => string
): SearchItem[] => {
  const [items, setItems] = useState<SearchItem[]>([])
  const router = useRouter()
  const orderSearched = router.query.returnUrl
    ? router.query.returnUrl.includes('order-search')
    : false
  const quoteSearched = router.query.returnUrl
    ? router.query.returnUrl.includes('quote-search')
    : false
  const pathSplit = router.pathname.split('/')
  const currentPath = pathSplit[pathSplit.length - 1]
    .toUpperCase()
    .replace(/-/g, '_')
  const { isOrderStatusCustomer, isExternalSalesAgent, isCustomerServiceRep } =
    useCurrentUser()
  const orderSearchRoute =
    !isOrderStatusCustomer || isExternalSalesAgent || isCustomerServiceRep
      ? orderCenterRoutes.orders()
      : orderCenterRoutes.orderSearch()
  const quoteURL = router.query.returnUrl
    ? router.query.returnUrl
    : orderCenterRoutes.quotes()

  useEffect(() => {
    const breadcrumbs = {
      '[ORDERID]': () => {
        if (orderSearched) {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              url: router?.query?.returnUrl?.includes('order-search')
                ? router.query.returnUrl
                : orderCenterRoutes.orderSearch(),
              label:
                !isOrderStatusCustomer ||
                isExternalSalesAgent ||
                isCustomerServiceRep
                  ? formatMessage(messages.ORDER_SEARCH)
                  : formatMessage(messages.ORDERS),
              gaLabel:
                !isOrderStatusCustomer ||
                isExternalSalesAgent ||
                isCustomerServiceRep
                  ? messages.ORDER_SEARCH.defaultMessage
                  : messages.ORDERS.defaultMessage,
            },
            {
              label: formatMessage(messages.ORDER_DETAILS),
              gaLabel: messages.ORDER_DETAILS.defaultMessage,
            },
          ])
        } else {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              url: orderSearchRoute,
              label: formatMessage(messages.ORDERS),
              gaLabel: messages.ORDERS.defaultMessage,
            },
            {
              label: formatMessage(messages.ORDER_DETAILS),
              gaLabel: messages.ORDER_DETAILS.defaultMessage,
            },
          ])
        }
      },
      '[RETURNID]': () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderSearchRoute,
            label: formatMessage(messages.ORDERS),
            gaLabel: messages.ORDERS.defaultMessage,
          },
          {
            label: formatMessage(mainMessages.ORDER_RETURN),
            gaLabel: mainMessages.ORDER_RETURN.defaultMessage,
          },
        ])
      },
      ['ORDER_SEARCH']: () => {
        if (
          !isOrderStatusCustomer ||
          isExternalSalesAgent ||
          isCustomerServiceRep
        ) {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              url: orderCenterRoutes.orders(),
              label: formatMessage(messages.ORDERS),
              gaLabel: messages.ORDERS.defaultMessage,
            },

            {
              label: `${
                messages[currentPath]
                  ? formatMessage(messages[currentPath])
                  : ''
              }`,
              gaLabel: `${
                messages[currentPath]
                  ? messages[currentPath].defaultMessage
                  : ''
              }`,
            },
          ])
        } else {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              label: formatMessage(messages.ORDERS),
              gaLabel: messages.ORDERS.defaultMessage,
            },
          ])
        }
      },
      ['QUOTE_SEARCH']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderCenterRoutes.quotes(),
            label: formatMessage({ id: 'QUOTES', defaultMessage: 'Quotes' }),
            gaLabel: 'Quotes',
          },
          {
            label: `${
              messages[currentPath] ? formatMessage(messages[currentPath]) : ''
            }`,
            gaLabel: messages[currentPath]
              ? messages[currentPath].defaultMessage
              : '',
          },
        ])
      },
      [messages.ORDERS.id]: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage(messages.ORDERS),
            gaLabel: messages.ORDERS.defaultMessage,
          },
        ])
      },
      ['QUOTES']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({ id: 'QUOTES', defaultMessage: 'Quotes' }),
            gaLabel: 'Quotes',
          },
        ])
      },
      ['CREATE_QUOTE']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderCenterRoutes.quotes(),
            label: formatMessage({ id: 'QUOTES', defaultMessage: 'Quotes' }),
            gaLabel: 'Quotes',
          },
          {
            label: formatMessage({
              id: 'CREATE_QUOTE',
              defaultMessage: 'Create Quote',
            }),
            gaLabel: 'Create Quote',
          },
        ])
      },
      '[QUOTEID]': () => {
        if (quoteSearched) {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              url: orderCenterRoutes.quotes(),
              label: formatMessage({ id: 'QUOTES', defaultMessage: 'Quotes' }),
              gaLabel: 'Quotes',
            },
            {
              url: orderCenterRoutes.searchReturn(),
              label: formatMessage(messages.QUOTE_SEARCH),
              gaLabel: messages.QUOTE_SEARCH.defaultMessage,
            },
            {
              label: formatMessage(messages.QUOTE_DETAILS),
              gaLabel: messages.QUOTE_DETAILS.defaultMessage,
            },
          ])
        } else {
          setItems([
            {
              url: orderCenterRoutes.dashboard(),
              label: formatMessage({
                id: 'ACCOUNT',
                defaultMessage: 'Account',
              }),
              gaLabel: mainMessages.ACCOUNT.defaultMessage,
            },
            {
              url: quoteURL,
              label: formatMessage({ id: 'QUOTES', defaultMessage: 'Quotes' }),
              gaLabel: 'Quotes',
            },
            {
              label: formatMessage(messages.QUOTE_DETAILS),
              gaLabel: messages.QUOTE_DETAILS.defaultMessage,
            },
          ])
        }
      },
      ['SAVED_CARTS']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'SAVED_CARTS',
              defaultMessage: 'Saved Carts',
            }),
            gaLabel: 'saved carts',
          },
        ])
      },
      '[CARTID]': () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderCenterRoutes.savedCarts(),
            label: formatMessage({
              id: 'SAVED_CARTS',
              defaultMessage: 'Saved Carts',
            }),
          },
          {
            label: formatMessage({
              id: 'SAVED_CART',
              defaultMessage: 'Saved Cart',
            }),
            gaLabel: 'saved carts',
          },
        ])
      },
      ['QUICK_ORDER']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'QUICK_ORDER',
              defaultMessage: 'Quick Order',
            }),
            gaLabel: 'quick order',
          },
        ])
      },
      ['BUY_IT_AGAIN']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'BUY_IT_AGAIN',
              defaultMessage: 'Buy It Again',
            }),
            gaLabel: 'buy it again',
          },
        ])
      },
      ['LISTS']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({ id: 'LISTS', defaultMessage: 'Lists' }),
            gaLabel: 'lists',
          },
        ])
      },
      '[LISTID]': () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderCenterRoutes.lists(),
            label: formatMessage({ id: 'LISTS', defaultMessage: 'Lists' }),
            gaLabel: 'lists',
          },
          {
            label: formatMessage(messages.LIST),
            gaLabel: messages.LIST.defaultMessage,
          },
        ])
      },
      ['INVOICES']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage(messages.PAY_INVOICES),
            gaLabel: messages.PAY_INVOICES.defaultMessage,
          },
        ])
      },
      ['INVOICE_PAYMENT']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            url: orderCenterRoutes.invoices(),
            label: formatMessage(messages.PAY_INVOICES),
            gaLabel: messages.PAY_INVOICES.defaultMessage,
          },
          {
            label: formatMessage(messages.INVOICE_PAYMENT),
            gaLabel: messages.INVOICE_PAYMENT.defaultMessage,
          },
        ])
      },
      ['PAYMENT_HISTORY']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage(messages.PAYMENT_HISTORY),
            gaLabel: messages.PAYMENT_HISTORY.defaultMessage,
          },
        ])
      },
      ['INVOICE_LIST']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'INVOICES',
              defaultMessage: 'Invoices',
            }),
            gaLabel: 'invoices',
          },
        ])
      },
      ['ACCOUNT']: () => {
        setItems([
          {
            url: homeRoute.index(),
            label: formatMessage({ id: 'HOME', defaultMessage: 'Home' }),
            gaLabel: mainMessages.HOME.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'ACCOUNT',
              defaultMessage: 'Account',
            }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
        ])
      },
      ['SUBSCRIPTIONS_LIST']: () => {
        setItems([
          {
            url: orderCenterRoutes.dashboard(),
            label: formatMessage({ id: 'ACCOUNT', defaultMessage: 'Account' }),
            gaLabel: mainMessages.ACCOUNT.defaultMessage,
          },
          {
            label: formatMessage({
              id: 'SUBSCRIPTIONS',
              defaultMessage: 'Subscriptions',
            }),
            gaLabel: 'subscriptions',
          },
        ])
      },
      default: () => {
        // Added comment for avoid es-lint error
      },
    }
    if (breadcrumbs[currentPath]) {
      breadcrumbs[currentPath]()
    } else {
      breadcrumbs['default']()
    }
  }, [currentPath, formatMessage])
  return items
}

export function shouldShowAccountAffiliationForm(
  currentUser: UserUnion | null,
  userSession: UserSession,
  uniqueSoldTos: number,
  userIsLoggedIn: boolean
) {
  const guestShoppingType = String(
    currentUser?.__typename === 'LoggedInUser' &&
      currentUser?.metadata?.guestShoppingType
  )
  const isBlueErpIntegrationEnabled =
    !!currentUser?.metadata?.isBlueErpIntegrationEnabled

  const { store } = userSession
  const noPartners = uniqueSoldTos < 1

  // WIBF-2488 SIAL store logged in user without partners in a guest shopping enabled country should see the functionality.
  return (
    userIsLoggedIn &&
    noPartners &&
    store &&
    store === 'sial' &&
    guestShoppingType !== 'null' &&
    !isBlueErpIntegrationEnabled
  )
}
